import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  fmt, groundData, findEGC, motor1phHeaders, motor1phRows, motor3phHeaders, motor3phRows,
  wireSizeData, ambientTempFactors, conduitFillFactors, VD_LOADS, VD_WIRE_SIZES,
} from "./electrical-helpers";
import { readSettings } from "@/hooks/use-settings";
import type { useJobLog } from "@/hooks/use-job-log";

type SaveFn = (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void;
const defaults = readSettings();

export const GroundLookup = ({ onSave }: { onSave?: SaveFn }) => {
  const [breakerInput, setBreakerInput] = useState("");
  const amps = parseInt(breakerInput, 10);
  const match = findEGC(amps);

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        NEC Table 250.122 — Enter your breaker/OCPD rating to find the minimum equipment grounding conductor.
      </p>

      <div>
        <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">
          Breaker / OCPD Rating (Amps)
        </label>
        <Input
          type="number"
          inputMode="numeric"
          value={breakerInput}
          onChange={(e) => setBreakerInput(e.target.value.slice(0, 5))}
          placeholder="e.g. 200"
          className="bg-secondary text-foreground border-border font-mono-data text-lg"
        />
      </div>

      {match && !isNaN(amps) && amps > 0 && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-primary/40 bg-primary/5">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Copper EGC</span>
              <span className="font-mono-data text-lg text-primary font-bold">{match.copper}</span>
            </div>
          </div>
          <div className="trade-card border-muted/40 bg-muted/5">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Aluminum EGC</span>
              <span className="font-mono-data text-lg text-foreground font-bold">{match.aluminum}</span>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Based on OCPD rating of <span className="font-mono-data font-bold text-foreground">{match.ocpd}A</span>
            {match.ocpd !== amps && <span> (next standard size ≥ {amps}A)</span>}
          </p>

          {onSave && (
            <button type="button" onClick={() => {
              onSave({
                calcType: "egc-lookup", label: `EGC for ${match.ocpd}A OCPD`,
                inputs: { ocpd: `${amps}A` },
                results: [
                  { label: "Copper EGC", value: match.copper, unit: "" },
                  { label: "Aluminum EGC", value: match.aluminum, unit: "" },
                  { label: "OCPD Rating", value: String(match.ocpd), unit: "A" },
                ],
                note: "",
              });
              toast.success("Saved to Job Log");
            }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
          )}
        </div>
      )}

      {!isNaN(amps) && amps > 0 && !match && (
        <div className="trade-card border-destructive/40 bg-destructive/5">
          <p className="text-sm text-destructive font-semibold">⚠️ Exceeds table range (max 6000A)</p>
          <p className="text-xs text-muted-foreground mt-1">Consult NEC 250.122 directly for sizes above 6000A.</p>
        </div>
      )}

      <h2 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-2">Full Table 250.122</h2>
      <div className="-mx-4 overflow-x-auto">
        <div className="mx-4 rounded-lg border border-border">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="data-table-header">OCPD (A)</th>
                <th className="data-table-header">Copper</th>
                <th className="data-table-header">Aluminum</th>
              </tr>
            </thead>
            <tbody>
              {groundData.map((row, i) => {
                const isMatch = match?.ocpd === row.ocpd && !isNaN(amps) && amps > 0;
                return (
                  <tr
                    key={i}
                    className={
                      isMatch
                        ? "bg-primary/15 ring-1 ring-inset ring-primary/30"
                        : i % 2 === 0
                        ? "bg-card"
                        : "bg-secondary/30"
                    }
                  >
                    <td className={`data-table-cell ${isMatch ? "text-primary font-bold" : ""}`}>{row.ocpd}</td>
                    <td className={`data-table-cell ${isMatch ? "text-primary font-bold" : ""}`}>{row.copper}</td>
                    <td className={`data-table-cell ${isMatch ? "text-primary font-bold" : ""}`}>{row.aluminum}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
        ⚠️ Where conductors are increased in size for voltage drop, the EGC must be proportionally increased per NEC 250.122(B).
      </p>
    </div>
  );
};

export const MotorFLA = () => {
  const [hpInput, setHpInput] = useState("");
  const hp = hpInput.trim();

  const match1ph = hp ? motor1phRows.findIndex((r) => String(r[0]) === hp) : -1;
  const match3ph = hp ? motor3phRows.findIndex((r) => String(r[0]) === hp) : -1;

  const matched3phRow = match3ph >= 0 ? motor3phRows[match3ph] : null;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        NEC Tables 430.248 (1Φ) & 430.250 (3Φ) — Motor full-load amps. Enter HP to highlight & calculate overload sizing.
      </p>

      <div>
        <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">
          Motor HP
        </label>
        <Input
          type="text"
          inputMode="text"
          value={hpInput}
          onChange={(e) => setHpInput(e.target.value.slice(0, 10))}
          placeholder="e.g. 10"
          className="bg-secondary text-foreground border-border font-mono-data text-lg"
        />
      </div>

      {matched3phRow && (
        <div className="space-y-2 animate-fade-in">
          <h2 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
            Overload Protection (125% per NEC 430.32)
          </h2>
          {[
            { label: "208V", fla: matched3phRow[1] as number },
            { label: "230V", fla: matched3phRow[2] as number },
            { label: "460V", fla: matched3phRow[3] as number },
          ].map(({ label, fla }) => (
            <div key={label} className="trade-card flex justify-between items-center">
              <span className="text-sm text-muted-foreground">{label} — Max OL Trip</span>
              <span className="font-mono-data text-primary font-bold">
                {fmt(fla * 1.25, 1)} <span className="text-xs text-muted-foreground font-normal">A</span>
              </span>
            </div>
          ))}
        </div>
      )}

      <h2 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-1">Single-Phase (Table 430.248)</h2>
      <div className="-mx-4 overflow-x-auto">
        <div className="mx-4 rounded-lg border border-border">
          <table className="w-full text-left">
            <thead>
              <tr>
                {motor1phHeaders.map((h) => <th key={h} className="data-table-header">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {motor1phRows.map((row, i) => {
                const isMatch = i === match1ph;
                return (
                  <tr key={i} className={isMatch ? "bg-primary/15 ring-1 ring-inset ring-primary/30" : i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                    {row.map((cell, j) => (
                      <td key={j} className={`data-table-cell ${isMatch ? "text-primary font-bold" : ""}`}>{cell}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <h2 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-1">Three-Phase (Table 430.250)</h2>
      <div className="-mx-4 overflow-x-auto">
        <div className="mx-4 rounded-lg border border-border">
          <table className="w-full text-left">
            <thead>
              <tr>
                {motor3phHeaders.map((h) => <th key={h} className="data-table-header">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {motor3phRows.map((row, i) => {
                const isMatch = i === match3ph;
                return (
                  <tr key={i} className={isMatch ? "bg-primary/15 ring-1 ring-inset ring-primary/30" : i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                    {row.map((cell, j) => (
                      <td key={j} className={`data-table-cell ${isMatch ? "text-primary font-bold" : ""}`}>{cell}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
        ⚠️ Overload protection set at 125% FLA per NEC 430.32. For motors with SF ≥ 1.15 or Class 10/20 insulation, may use up to 140%. Wire sizing per NEC 430.22 = 125% × FLA.
      </p>
    </div>
  );
};

export const WireSizing = ({ onSave }: { onSave?: SaveFn }) => {
  const [amps, setAmps] = useState("");
  const [distance, setDistance] = useState("");
  const [voltage, setVoltage] = useState(defaults.voltage);
  const [phases, setPhases] = useState<string>(defaults.phases);
  const [material, setMaterial] = useState<string>(defaults.conductorMaterial);
  const [maxDrop, setMaxDrop] = useState("3");
  const [ambientTemp, setAmbientTemp] = useState("30");
  const [conduitFill, setConduitFill] = useState("3");

  const loadAmps = parseFloat(amps);
  const distFt = parseFloat(distance);
  const volt = parseFloat(voltage);
  const dropPct = parseFloat(maxDrop);
  const phaseFactor = phases === "3" ? 1.732 : 2;

  const tempFactor = ambientTempFactors.find((t) => t.value === ambientTemp)?.factor ?? 1.0;
  const fillFactor = conduitFillFactors.find((c) => c.value === conduitFill)?.factor ?? 1.0;
  const combinedDerate = tempFactor * fillFactor;

  const isValid = loadAmps > 0 && distFt > 0 && volt > 0 && dropPct > 0;

  const ampacityMin = isValid
    ? wireSizeData.find((w) => {
        const cap = material === "cu" ? w.cuAmps : w.alAmps;
        return cap > 0 && cap * combinedDerate >= loadAmps;
      })
    : null;

  const vdMin = isValid
    ? wireSizeData.find((w) => {
        const res = material === "cu" ? w.cuRes : w.alRes;
        if (res <= 0) return false;
        const vDrop = (loadAmps * res * distFt * phaseFactor) / 1000;
        const vDropPct = (vDrop / volt) * 100;
        return vDropPct <= dropPct;
      })
    : null;

  const getRecommendation = () => {
    if (!ampacityMin && !vdMin) return null;
    if (!ampacityMin) return { wire: vdMin!, reason: "voltage drop" };
    if (!vdMin) return { wire: ampacityMin!, reason: "ampacity" };
    const ampIdx = wireSizeData.indexOf(ampacityMin);
    const vdIdx = wireSizeData.indexOf(vdMin);
    return vdIdx > ampIdx
      ? { wire: vdMin, reason: "voltage drop" }
      : { wire: ampacityMin, reason: "ampacity" };
  };

  const recommendation = isValid ? getRecommendation() : null;

  const actualVd = recommendation
    ? (() => {
        const res = material === "cu" ? recommendation.wire.cuRes : recommendation.wire.alRes;
        const vDrop = (loadAmps * res * distFt * phaseFactor) / 1000;
        return { volts: vDrop, pct: (vDrop / volt) * 100 };
      })()
    : null;

  const deratedAmpacity = recommendation
    ? (material === "cu" ? recommendation.wire.cuAmps : recommendation.wire.alAmps) * combinedDerate
    : null;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Calculates minimum wire gauge based on NEC Table 310.16 ampacity (75°C) with ambient temperature &amp; conduit fill derating, plus voltage drop.
      </p>

      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Load Amps</label>
          <Input type="number" inputMode="decimal" value={amps} onChange={(e) => setAmps(e.target.value.slice(0, 6))} placeholder="e.g. 40" className="bg-secondary text-foreground border-border font-mono-data text-lg" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Distance (ft, one-way)</label>
          <Input type="number" inputMode="decimal" value={distance} onChange={(e) => setDistance(e.target.value.slice(0, 6))} placeholder="e.g. 150" className="bg-secondary text-foreground border-border font-mono-data text-lg" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-x-2 gap-y-2">
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Voltage</label>
          <Select value={voltage} onValueChange={setVoltage}>
            <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["120", "208", "240", "277", "480", "600"].map((v) => (
                <SelectItem key={v} value={v}>{v}V</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Phases</label>
          <Select value={phases} onValueChange={setPhases}>
            <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1Φ</SelectItem>
              <SelectItem value="3">3Φ</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Material</label>
          <Select value={material} onValueChange={setMaterial}>
            <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cu">Copper</SelectItem>
              <SelectItem value="al">Aluminum</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-x-2 gap-y-2">
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Max VD</label>
          <Select value={maxDrop} onValueChange={setMaxDrop}>
            <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["2", "3", "5"].map((v) => (
                <SelectItem key={v} value={v}>{v}%</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Amb. °</label>
          <Select value={ambientTemp} onValueChange={setAmbientTemp}>
            <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ambientTempFactors.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block"># in Pipe</label>
          <Select value={conduitFill} onValueChange={setConduitFill}>
            <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {conduitFillFactors.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {combinedDerate < 1.0 && (
        <div className="trade-card border-accent/40 bg-accent/5">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">Combined Derating Factor</span>
            <span className="font-mono-data text-sm font-bold text-foreground">
              {fmt(tempFactor, 2)} × {fmt(fillFactor, 2)} = <span className="text-primary">{fmt(combinedDerate, 2)}</span>
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">
            Temp: NEC 310.15(B)(1) • Fill: NEC 310.15(C)(1)
          </p>
        </div>
      )}

      {recommendation && actualVd && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-primary/40 bg-primary/5">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Recommended Wire</span>
              <span className="font-mono-data text-xl text-primary font-bold">{recommendation.wire.label}</span>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Governing factor: <span className="font-semibold text-foreground">{recommendation.reason}</span>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="trade-card">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Derated Ampacity</span>
              <span className="font-mono-data text-lg font-bold text-foreground">
                {fmt(deratedAmpacity ?? 0, 0)}A
              </span>
              {combinedDerate < 1.0 && (
                <span className="text-[10px] text-muted-foreground block">
                  ({material === "cu" ? recommendation.wire.cuAmps : recommendation.wire.alAmps}A × {fmt(combinedDerate, 2)})
                </span>
              )}
            </div>
            <div className="trade-card">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Voltage Drop</span>
              <span className="font-mono-data text-lg font-bold text-foreground">
                {fmt(actualVd.volts, 1)}V <span className="text-sm text-muted-foreground">({fmt(actualVd.pct, 1)}%)</span>
              </span>
            </div>
          </div>

          {ampacityMin && vdMin && (
            <div className="grid grid-cols-2 gap-2">
              <div className="trade-card border-muted/40">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Min by Ampacity</span>
                <span className="font-mono-data text-sm font-bold text-foreground">{ampacityMin.label}</span>
              </div>
              <div className="trade-card border-muted/40">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Min by VD ({maxDrop}%)</span>
                <span className="font-mono-data text-sm font-bold text-foreground">{vdMin.label}</span>
              </div>
            </div>
          )}

          {onSave && (
            <button type="button" onClick={() => {
              onSave({
                calcType: "wire-sizing", label: `${recommendation.wire.label} ${material === "cu" ? "Cu" : "Al"} — ${loadAmps}A, ${distFt}ft, ${voltage}V ${phases}Φ`,
                inputs: { amps: `${loadAmps}A`, distance: `${distFt}ft`, voltage: `${voltage}V`, phases: `${phases}Φ`, material: material === "cu" ? "Copper" : "Aluminum", maxVD: `${maxDrop}%` },
                results: [
                  { label: "Wire Size", value: recommendation.wire.label, unit: "" },
                  { label: "Voltage Drop", value: fmt(actualVd.pct, 1), unit: "%" },
                  { label: "Derated Ampacity", value: fmt(deratedAmpacity ?? 0, 0), unit: "A" },
                  { label: "Governing Factor", value: recommendation.reason, unit: "" },
                ],
                note: "",
              });
              toast.success("Saved to Job Log");
            }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
          )}
        </div>
      )}

      {isValid && !recommendation && (
        <div className="trade-card border-destructive/40 bg-destructive/5">
          <p className="text-sm text-destructive font-semibold">⚠️ No wire in table meets these requirements</p>
          <p className="text-xs text-muted-foreground mt-1">Consider parallel runs or consult NEC tables for larger conductors.</p>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
        ⚠️ Based on NEC Table 310.16 (75°C, THWN-2) with correction factors from Tables 310.15(B)(1) &amp; 310.15(C)(1). Always verify with local codes.
      </p>
    </div>
  );
};

export const VoltageDropTable = () => {
  const [voltage, setVoltage] = useState("240");
  const [phases, setPhases] = useState("1");
  const [maxDrop, setMaxDrop] = useState("3");

  const V = parseFloat(voltage);
  const K = phases === "3" ? 1.732 : 2;
  const dropPct = parseFloat(maxDrop) / 100;

  const rows = VD_WIRE_SIZES.map((w) => {
    const distances = VD_LOADS.map((amps) => {
      const maxDist = (dropPct * V * 1000) / (amps * w.cuRes * K);
      return Math.floor(maxDist);
    });
    return { ...w, distances };
  });

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Maximum one-way wire run distance (feet) for copper conductors at various loads, keeping voltage drop within the selected limit.
      </p>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Voltage</label>
          <Select value={voltage} onValueChange={setVoltage}>
            <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["120", "208", "240", "277", "480"].map((v) => (
                <SelectItem key={v} value={v}>{v}V</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Phases</label>
          <Select value={phases} onValueChange={setPhases}>
            <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1Φ</SelectItem>
              <SelectItem value="3">3Φ</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Max VD (%)</label>
          <Select value={maxDrop} onValueChange={setMaxDrop}>
            <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="2">2%</SelectItem>
              <SelectItem value="3">3%</SelectItem>
              <SelectItem value="5">5%</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="-mx-4 overflow-x-auto">
        <div className="mx-4 rounded-lg border border-border">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="data-table-header sticky left-0 bg-card z-10">Wire</th>
                {VD_LOADS.map((a) => (
                  <th key={a} className="data-table-header text-center">{a}A</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                  <td className="data-table-cell font-semibold sticky left-0 bg-inherit z-10">{row.label}</td>
                  {row.distances.map((d, j) => (
                    <td key={j} className={`data-table-cell font-mono-data text-center ${d < 50 ? "text-destructive" : d < 100 ? "text-safety" : ""}`}>
                      {d > 0 ? d : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
        ⚠️ Distances in feet (one-way). Copper conductors, THHN/THWN-2. VD = I × R × L × K / 1000. Red = &lt;50ft, yellow = &lt;100ft. NEC recommends ≤3% branch, ≤5% total.
      </p>
    </div>
  );
};
